import {Observable, ReplaySubject} from "rxjs";
import {DeliveryPoint} from "@app/core/delivery-point/application/dtos/delivery-point";
import {HandlerBuilder, HttpMethod} from "@app/core/shared/infrastructure/base.handler/base-handle.parameters";
import {ApiUrls} from "@app/core/shared/infrastructure/api-urls";
import {catchError, map, switchMap, tap} from "rxjs/operators";
import {BaseHandleErrorMapper} from "@app/core/shared/infrastructure/base.handler/base-handle-error.mapper";
import {EuropastryBaseHandler} from "@app/core/shared/infrastructure/base.handler/europastry-base.handler";
import {DeliveryPointRemote} from "@app/core/delivery-point/domain/delivery-point.remote";
import {DeliveryPointMapper} from "@app/core/delivery-point/infrastructure/mappers/delivery-point.mapper";
import {Injectable} from "@angular/core";
import {CredentialsStorage} from "@app/core/credentials/domain/credentials-storage";
import {ThirdPartyApisUrls} from "@app/core/shared/infrastructure/third-party-apis-urls";
import {CreateDeliveryPointData} from "@app/core/delivery-point/application/dtos/create-delivery-point-data";
import {
    CreateDeliveryPointDataMapper
} from "@app/core/delivery-point/infrastructure/mappers/create-delivery-point-data.mapper";
import {Credentials} from "@app/core/credentials/domain/credentials";
import {DeliveryPointType} from "@app/core/delivery-point/application/dtos/delivery-point-type";
import {OrderTypeMapper} from "@app/core/delivery-point/infrastructure/mappers/order-type.mapper";

@Injectable({
    providedIn: 'root'
})
export class DeliveryPointWebService implements DeliveryPointRemote {

    private refresh = new ReplaySubject<void>();

    constructor(
        private baseHandler: EuropastryBaseHandler,
        private credentialsStorage: CredentialsStorage,
        private apiUrls: ApiUrls,
    ) { }

    private reloadDeliveryPoints() {
        this.refresh.next();
    }

    getDeliveryPoints(credentials?: Credentials): Observable<DeliveryPoint[]> {
        const parameters = new HandlerBuilder()
            .method(HttpMethod.get)
            .url(this.apiUrls.DELIVERY_POINTS)
            .authorization(credentials ?? this.credentialsStorage.getCredentials())
            .build()

        const mappedResponse = this.baseHandler.execute(parameters).pipe(
            map(response => response.results),
            map(deliveryPoints => deliveryPoints.map(DeliveryPointMapper.deserialize)),
            catchError(BaseHandleErrorMapper.handleError),
        )

        this.reloadDeliveryPoints();
        return this.refresh.asObservable().pipe(switchMap(() => { return mappedResponse }));
    }

    /**
     * Get postal code from coordinates
     * @param latitude
     * @param longitude
     */
    getPostalCodeFromCoordinates(latitude: number, longitude: number): Observable<string> {
        const parametersOSM = new HandlerBuilder()
            .method(HttpMethod.get)
            .url(ThirdPartyApisUrls.NOMINATIM)
            .params(new Map()
                .set('format', 'json')
                .set('lat', latitude)
                .set('lon', longitude)
            )
            .build()

        return this.baseHandler.execute(parametersOSM).pipe(
            map(response => response.address.postcode)
        );
    }

    deleteDeliveryPoint(deliveryPointCode: number): Observable<void> {
        const parameters = new HandlerBuilder()
            .method(HttpMethod.delete)
            .url(this.apiUrls.DELIVERY_POINTS + deliveryPointCode)
            .authorization(this.credentialsStorage.getCredentials())
            .build()

        return this.baseHandler.execute(parameters).pipe(
            catchError(BaseHandleErrorMapper.handleError),
            tap(() => this.reloadDeliveryPoints())
        )
    }

    createDeliveryPoint(createDeliveryPointData: CreateDeliveryPointData, temporalCredentials?: Credentials): Observable<DeliveryPoint> {
        const parameters = new HandlerBuilder()
            .method(HttpMethod.post)
            .url(this.apiUrls.DELIVERY_POINTS)
            .body(CreateDeliveryPointDataMapper.serialize(createDeliveryPointData))
            .authorization(temporalCredentials ?? this.credentialsStorage.getCredentials()) //If there aren't temporal credentials, use the stored ones.
            .build()

        return this.baseHandler.execute(parameters).pipe(
            catchError(BaseHandleErrorMapper.handleError),
            tap(() => this.reloadDeliveryPoints())
        )
    }

    saveDeliveryPoint(deliveryPointCode: number, credentials?: Credentials): Observable<void> {
        const parameters = new HandlerBuilder()
            .method(HttpMethod.get)
            .url(this.apiUrls.DELIVERY_POINTS + deliveryPointCode)
            .authorization( credentials ?? this.credentialsStorage.getCredentials())
            .build()

        return this.baseHandler.execute(parameters).pipe(
            catchError(BaseHandleErrorMapper.handleError)
        )
    }

    getDeliveryPoint(deliveryPointCode: number): Observable<DeliveryPoint> {
        const parameters = new HandlerBuilder()
            .method(HttpMethod.get)
            .url(this.apiUrls.DELIVERY_POINTS + deliveryPointCode)
            .authorization(this.credentialsStorage.getCredentials())
            .build()

        return this.baseHandler.execute(parameters).pipe(
            catchError(BaseHandleErrorMapper.handleError),
            map(response => response.results),
            map(DeliveryPointMapper.deserialize),
        );
    }

    getDeliveryPointType(deliveryPointCode: number): Observable<DeliveryPointType> {
        const parameters = new HandlerBuilder()
            .method(HttpMethod.get)
            .url(this.apiUrls.ORDER_TYPES + deliveryPointCode)
            .authorization(this.credentialsStorage.getCredentials())
            .build()

        return this.baseHandler.execute(parameters).pipe(
            catchError(BaseHandleErrorMapper.handleError),
            map(response => response.results),
            map(OrderTypeMapper.deserialize),
        );
    }

}
